import { Injectable, inject } from '@angular/core';
import { LangGraphClientFactory } from '../../core/agents/langgraph-client.factory';
import { socAgentBaseUrlActiva } from '../../core/agents/agent-registry';
import { parseDocumentArtifact } from './documento.util';
import { DocumentArtifact, DocumentoFormato, DocumentosCapacidades } from './documento.types';

/** Error de las rutas `/documentos/*`: `{"error": "<codigo>", "detalle": "texto"}`. */
export class DocumentosApiError extends Error {
  constructor(readonly status: number, readonly codigo: string, readonly detalle: string) {
    super(detalle || codigo || `HTTP ${status}`);
  }
}

const SIN_CAPACIDADES: DocumentosCapacidades = {
  enabled: false, formatos: [], exportarFormatos: [], plantillas: [],
};

/** Tras un fallo de `capacidades()` no se vuelve a preguntar hasta pasado este tiempo. */
const REINTENTO_CAPACIDADES_MS = 30_000;

/**
 * Rutas HTTP del soc-agent para documentos (`/documentos/*`). Mismo host y mismo
 * Bearer de Auth0 que el SDK de LangGraph: se reutiliza el `authFetch` de la fábrica.
 */
@Injectable({ providedIn: 'root' })
export class DocumentosApiService {
  private readonly factory = inject(LangGraphClientFactory);
  private capacidades$: Promise<DocumentosCapacidades> | null = null;
  private falloEn = 0;

  /**
   * Qué puede hacer el servidor. Cacheada por sesión; ante cualquier error responde
   * «desactivado» (la UI esconde el botón en vez de romperse) y reintenta pasados
   * unos segundos, para que un 503 de arranque no deje la función apagada hasta
   * recargar.
   */
  capacidades(): Promise<DocumentosCapacidades> {
    if (this.capacidades$ && !(this.falloEn && Date.now() - this.falloEn > REINTENTO_CAPACIDADES_MS)) {
      return this.capacidades$;
    }
    this.falloEn = 0;
    this.capacidades$ = this.pedirCapacidades();
    return this.capacidades$;
  }

  private async pedirCapacidades(): Promise<DocumentosCapacidades> {
    try {
      const r = await this.factory.authFetch()(`${socAgentBaseUrlActiva()}/documentos/capacidades`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json() as Record<string, unknown>;
      const lista = (v: unknown): DocumentoFormato[] =>
        Array.isArray(v) ? v.filter((f): f is DocumentoFormato => f === 'pdf' || f === 'docx' || f === 'pptx') : [];
      return {
        enabled: j['enabled'] === true,
        formatos: lista(j['formatos']),
        exportarFormatos: lista(j['exportar_formatos']),
        plantillas: Array.isArray(j['plantillas']) ? j['plantillas'].filter((p): p is string => typeof p === 'string') : [],
      };
    } catch {
      this.falloEn = Date.now();
      return SIN_CAPACIDADES;
    }
  }

  exportarHilo(threadId: string, format: DocumentoFormato, signal?: AbortSignal): Promise<DocumentArtifact> {
    return this.post('/documentos/exportar-hilo', { thread_id: threadId, format }, signal);
  }

  /** Una respuesta del agente como documento. `text` es lo que se ve de ella: el servidor la
   *  localiza en el hilo por su principio, así que basta con el arranque. */
  exportarRespuesta(
    threadId: string, format: DocumentoFormato, text: string, signal?: AbortSignal,
  ): Promise<DocumentArtifact> {
    return this.post('/documentos/exportar-respuesta', { thread_id: threadId, format, text }, signal);
  }

  cambiarFormato(id: string, format: DocumentoFormato): Promise<DocumentArtifact> {
    return this.post(`/documentos/${encodeURIComponent(id)}/formato`, { format });
  }

  muestra(format: DocumentoFormato): Promise<DocumentArtifact> {
    return this.post('/documentos/muestra', { format });
  }

  private async post(ruta: string, cuerpo: unknown, signal?: AbortSignal): Promise<DocumentArtifact> {
    const r = await this.factory.authFetch()(`${socAgentBaseUrlActiva()}${ruta}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cuerpo),
      signal,
    });
    let json: unknown = null;
    try { json = await r.json(); } catch { /* cuerpo vacío o no JSON */ }
    if (!r.ok) {
      const e = (json ?? {}) as Record<string, unknown>;
      throw new DocumentosApiError(
        r.status,
        typeof e['error'] === 'string' ? e['error'] : '',
        typeof e['detalle'] === 'string' ? e['detalle'] : '',
      );
    }
    const doc = parseDocumentArtifact(json);
    if (!doc) throw new DocumentosApiError(502, 'respuesta_invalida', '');
    return doc;
  }
}

/** Clave i18n del mensaje para un error de las rutas `/documentos/*` o de GraphQL. */
export function claveErrorDocumentos(err: unknown): string {
  const status = err instanceof DocumentosApiError ? err.status : 0;
  if (status === 404) return 'agent.documentos.noDisponible';
  if (status === 429) return 'agent.documentos.errorCuota';
  if (status === 503) return 'agent.documentos.errorDesactivado';
  return 'agent.documentos.errorGenerico';
}
